import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { jwtDecode } from "jwt-decode";
import { useStore } from "../../stores/root-store-context";
import ShortLayout from "../../components/layout/ShortLayout";
import KanbanBoardPage from "../KanbanTasks/KanbanBoardPage";
import ProjectTabs from "../Projects/ProjectTabs";
import ProjectPage from "../Projects/ProjectPage";
import { ProjectProvider, projectFromPublic } from "../Projects/ProjectContext";
import type { CurrentProject } from "../Projects/ProjectContext";
import type { Project } from "../../stores/ProjectsStore";

/** The board token names the project it opens (`JwtTokenService.GenerateToken`: claim `projectId`). */
function projectIdFromToken(token: string): string | undefined {
  try {
    const decoded = jwtDecode<{ projectId?: string | number | null }>(token);
    return decoded.projectId === undefined || decoded.projectId === null ? undefined : String(decoded.projectId);
  } catch (err) {
    console.error("JWT decode error:", err);
    return undefined;
  }
}

const AnonymousPage = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { anonymousStore, apiStore, kanbanTaskStore } = useStore();

  const [activeTab, setActiveTab] = useState<'info' | 'tasks'>('info');
  const [project, setProject] = useState<Project | undefined>();
  const [isProjectLoading, setIsProjectLoading] = useState(true);

  // Which tab is showing follows the URL on its own; folding this into the load effect below is
  // what made every drawer open and close re-initialise the whole board.
  useEffect(() => {
    setActiveTab(location.pathname.includes("/tasks") ? "tasks" : "info");
  }, [location.pathname]);

  const token = new URLSearchParams(location.search).get("token");
  const projectId = useMemo(() => (token ? projectIdFromToken(token) : undefined), [token]);

  /**
   * Configured during *render*, deliberately, not in an effect.
   *
   * React runs a commit's effect cleanups before its effects, child before parent. Doing this in an
   * effect therefore left a window on every re-run: the cleanup below put the store back into
   * `authenticated`, then the children's effects fired -- sending `/api/KanbanTask/...` without a
   * token and collecting a 401 -- and only then did the parent effect restore `public`. Render
   * always precedes child effects, so this ordering holds on mount, on remount and on a dependency
   * change alike. Both calls are idempotent setters, which is what makes them safe here.
   */
  useMemo(() => {
    if (!token || !projectId) return null;
    apiStore.setConfigApi({ token, companyId: "0", userId: 0 });
    // One store for both boards: from here on every kanban call goes to /api/OpenProjectApi/...
    kanbanTaskStore.setMode('public');
    return null;
  }, [token, projectId, apiStore, kanbanTaskStore]);

  useEffect(() => {
    if (!token || !projectId) {
      navigate('/link-expired', { replace: true });
      return;
    }

    let cancelled = false;

    const initData = async () => {
      try {
        // The statuses call doubles as the link check, and it is the *only* call that may decide
        // the link is dead: an expired or foreign token answers 401 here, and the 401 interceptor
        // leaves /api/OpenProjectApi/ requests alone (no session reset).
        await kanbanTaskStore.getProjectStatuses(projectId);
      } catch (error) {
        console.error("Token is invalid or the status request failed, redirecting to expired page...", error);
        if (!cancelled) {
          setIsProjectLoading(false);
          navigate('/link-expired', { replace: true });
        }
        return;
      }

      // Neither of these says anything about the link. A failed task list leaves an empty board and
      // a missing project (a deployment without GET /api/OpenProjectApi/project, or a transient
      // failure) leaves the header without a title — every reader guards on `project?.title`. So
      // they log and degrade instead of bouncing a valid share link to /link-expired.
      await Promise.all([
        kanbanTaskStore.getProjectTasks(projectId).catch((error: unknown) => {
          console.error("Failed to load the public task list", error);
        }),
        kanbanTaskStore
          .getPublicProject()
          .then((dto) => {
            if (!cancelled) setProject(projectFromPublic(dto));
          })
          .catch((error: unknown) => {
            console.error("Failed to load the public project", error);
          }),
      ]);

      if (!cancelled) setIsProjectLoading(false);
    };

    void initData();

    return () => {
      cancelled = true;
    };
    // Keyed on the token alone: keying on the path or query re-read the board (whose rows carry no
    // description) on every drawer open and filter change.
  }, [token, projectId, apiStore, kanbanTaskStore, navigate]);

  // Leaving the public board hands the store back to the authenticated app. Kept apart from the
  // load effect on purpose: as part of it, this ran on every dependency change and re-opened the
  // very window the render-phase configuration above exists to close.
  useEffect(() => () => kanbanTaskStore.setMode('authenticated'), [kanbanTaskStore]);

  const reloadProject = useCallback(async () => {
    const next = projectFromPublic(await kanbanTaskStore.getPublicProject());
    setProject(next);
    return next;
  }, [kanbanTaskStore]);

  const currentProject = useMemo<CurrentProject>(
    () => ({
      project,
      projectId,
      isLoading: isProjectLoading,
      isAnonymous: true,
      reload: reloadProject,
    }),
    [project, projectId, isProjectLoading, reloadProject],
  );

  return (
    <ProjectProvider value={currentProject}>
      <ShortLayout headerStyle={1} footerStyle={2}>
        <div style={{ padding: 20 }}>
          <ProjectTabs
            isAnonymous={true}
            onTabChange={(key) => setActiveTab(key as 'info' | 'tasks')}
          />

          {activeTab === 'tasks' ? (
            <KanbanBoardPage isAnonymous />
          ) : (
            <ProjectPage
              isShowInternalLoader={false}
              isAnonymous={true}
              getDocumentationByProjectProp={anonymousStore.getDocumentationByProject}
            />
          )}
        </div>
      </ShortLayout>
    </ProjectProvider>
  );
});

export default AnonymousPage;
